import React, { useState, useEffect } from 'react';
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Container,
  Card,
  CardContent,
  Grid,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  InputAdornment,
} from '@mui/material';
import {
  Logout,
  CalendarMonth,
  AdminPanelSettings,
  Assessment,
  LockReset,
  Visibility,
  VisibilityOff,
  Payments,
  School,
  AccountBalanceWallet,
  HelpOutline,
  Storefront,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { systemConfigAPI, authAPI, helpAPI } from '../services/api';

const Dashboard = () => {
  const { user, logout, hasAnyPermission } = useAuth();
  const navigate = useNavigate();
  const [logoPath, setLogoPath] = useState('');
  const [openPasswordDialog, setOpenPasswordDialog] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    // Fetch logo
    systemConfigAPI.getCurrentLogo()
      .then(response => {
        if (response.data.logo_path) {
          setLogoPath(`${process.env.REACT_APP_API_BASE_URL}/${response.data.logo_path}`);
        }
      })
      .catch(console.error);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleDownloadHelp = async () => {
    try {
      const response = await helpAPI.downloadPdf();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'attendance_payroll_help.pdf');
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Failed to download help document', err);
    }
  };

  const handleOpenPasswordDialog = () => {
    setOpenPasswordDialog(true);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleClosePasswordDialog = () => {
    setOpenPasswordDialog(false);
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setPasswordError('');
    setPasswordSuccess('');
  };

  const handleChangePassword = async () => {
    setPasswordError('');
    setPasswordSuccess('');

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      setPasswordError('All fields are required');
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('New password must be at least 6 characters long');
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      return;
    }

    if (currentPassword === newPassword) {
      setPasswordError('New password must be different from current password');
      return;
    }

    setLoading(true);

    try {
      await authAPI.changePassword(currentPassword, newPassword);
      setPasswordSuccess('Password changed successfully!');
      setTimeout(() => {
        handleClosePasswordDialog();
      }, 2000);
    } catch (error) {
      setPasswordError(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ flexGrow: 1 }}>
      <AppBar position="static">
        <Toolbar>
          {logoPath && (
            <Box
              component="img"
              src={logoPath}
              alt="Logo"
              sx={{ height: 40, mr: 2 }}
              onError={() => setLogoPath('')}
            />
          )}
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            ISKCON Hubli-Dharwad Applications
          </Typography>
          <Typography variant="body1" sx={{ mr: 2 }}>
            {user?.full_name} ({user?.role_name})
          </Typography>
          <IconButton color="inherit" onClick={handleDownloadHelp} title="Help">
            <HelpOutline />
          </IconButton>
          <IconButton
            color="inherit"
            onClick={handleOpenPasswordDialog}
            title="Change Password"
          >
            <LockReset />
          </IconButton>
          <IconButton color="inherit" onClick={handleLogout} title="Logout">
            <Logout />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Typography variant="h4" gutterBottom>
          Welcome, {user?.full_name}!
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Select an option below to get started
        </Typography>

        <Grid container spacing={3} sx={{ mt: 2 }}>
          {hasAnyPermission('attendance') && (
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  transition: 'box-shadow 0.3s',
                }}
                onClick={() => navigate('/attendance')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <CalendarMonth sx={{ fontSize: 60, color: 'primary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    View Attendance
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    View employee attendance reports for any month
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {hasAnyPermission('admin') && (
            <Grid item xs={12} md={6}>
              <Card 
                sx={{ 
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  transition: 'box-shadow 0.3s',
                }}
                onClick={() => navigate('/admin')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <AdminPanelSettings sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Admin Portal
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Manage departments, employees, holidays, and more
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* The tile links to /payroll (Generate Pay), which the route guard
              requires payroll.generate for specifically - gate on that exact
              key here too so a role with only payroll.configure_salary
              doesn't see a tile that immediately bounces them back. */}
          {hasAnyPermission('payroll.generate') && (
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  transition: 'box-shadow 0.3s',
                }}
                onClick={() => navigate('/payroll')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Payments sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Payroll
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Salary statements and bank letters
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Tile links to /folk-saptapur (Service Tasks), which the route guard
              requires folk_saptapur.service_tasks for specifically - gate on
              that exact key here too, same fix as the Payroll tile above. */}
          {hasAnyPermission('folk_saptapur.service_tasks') && (
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  transition: 'box-shadow 0.3s',
                }}
                onClick={() => navigate('/folk-saptapur')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <School sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Folk Saptapur
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Student sadhana attendance and service tasks
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {hasAnyPermission('salary_advance.') && (
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  transition: 'box-shadow 0.3s',
                }}
                onClick={() => navigate('/salary-advance')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <AccountBalanceWallet sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Salary Advance
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Track employee advances and EMI deductions
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}

          {hasAnyPermission('vendor_payments.manage') && (
            <Grid item xs={12} md={6}>
              <Card
                sx={{
                  cursor: 'pointer',
                  '&:hover': { boxShadow: 6 },
                  transition: 'box-shadow 0.3s',
                }}
                onClick={() => navigate('/vendor-payments')}
              >
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Storefront sx={{ fontSize: 60, color: 'secondary.main', mb: 2 }} />
                  <Typography variant="h5" gutterBottom>
                    Vendor Payments
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Track vendors and payments made to them
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        <Box sx={{ mt: 4, p: 3, bgcolor: 'background.paper', borderRadius: 1 }}>
          <Typography variant="h6" gutterBottom>
            Quick Information
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <Assessment sx={{ color: 'primary.main', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Current Month
                </Typography>
                <Typography variant="h6">
                  {new Date().toLocaleString('default', { month: 'long', year: 'numeric' })}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <CalendarMonth sx={{ color: 'success.main', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Today's Date
                </Typography>
                <Typography variant="h6">
                  {new Date().toLocaleDateString()}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} md={4}>
              <Box sx={{ p: 2, border: '1px solid #e0e0e0', borderRadius: 1 }}>
                <AdminPanelSettings sx={{ color: 'warning.main', mb: 1 }} />
                <Typography variant="body2" color="textSecondary">
                  Your Role
                </Typography>
                <Typography variant="h6" sx={{ textTransform: 'capitalize' }}>
                  {user?.role_name}
                </Typography>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Container>

      {/* Change Password Dialog */}
      <Dialog open={openPasswordDialog} onClose={handleClosePasswordDialog} maxWidth="sm" fullWidth>
        <DialogTitle>Change Password</DialogTitle>
        <DialogContent>
          {passwordError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {passwordError}
            </Alert>
          )}
          {passwordSuccess && (
            <Alert severity="success" sx={{ mb: 2 }}>
              {passwordSuccess}
            </Alert>
          )}
          
          <TextField
            margin="normal"
            fullWidth
            label="Current Password"
            type={showCurrentPassword ? 'text' : 'password'}
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    edge="end"
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            margin="normal"
            fullWidth
            label="New Password"
            type={showNewPassword ? 'text' : 'password'}
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            disabled={loading}
            helperText="Must be at least 6 characters"
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    edge="end"
                  >
                    {showNewPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
          
          <TextField
            margin="normal"
            fullWidth
            label="Confirm New Password"
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    edge="end"
                  >
                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClosePasswordDialog} disabled={loading}>
            Cancel
          </Button>
          <Button 
            onClick={handleChangePassword} 
            variant="contained" 
            disabled={loading}
          >
            {loading ? 'Changing...' : 'Change Password'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
